import { apiClient } from "@/lib/api-client";
import { API_URL } from "@/config/api";
import { downloadBlob } from "@/lib/utils";

/**
 * Download an order's invoice PDF — or, when running as an installed PWA,
 * open it in a browser view instead.
 *
 * Installed PWAs (iOS standalone especially) have no download manager:
 * blob/data URLs with a download attribute silently do nothing, and
 * window.open() is unreliable there too (often returns null and opens
 * nothing). A real anchor click is what iOS dependably routes to the
 * in-app browser sheet, where the PDF viewer offers share/save.
 *
 * Returns false on failure so callers can show a toast.
 */
export async function downloadInvoice(orderId: string): Promise<boolean> {
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true;
  if (isStandalone) {
    const a = document.createElement("a");
    a.href = `${API_URL}/orders/${orderId}/invoice/public`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
  }

  try {
    const response = await fetch(`${API_URL}/orders/${orderId}/invoice`, {
      headers: { Authorization: `Bearer ${apiClient.getToken()}` },
    });

    if (!response.ok) {
      throw new Error("Failed to download invoice");
    }

    const blob = await response.blob();
    await downloadBlob(blob, `invoice-${orderId}.pdf`);
    return true;
  } catch (error) {
    console.error("Error downloading invoice:", error);
    return false;
  }
}
